import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Drawer, TextField, Button, Table,
  TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Checkbox, MenuItem, IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { AppColors } from '../../../../util/AppColors';
import DayWiseTimeSlots from './DayWiseTimeSlots';
import dayjs from 'dayjs';
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomButton from "../../../../components/CustomButton";
import { useDispatch, useSelector } from 'react-redux';
import { getRoleList } from '../../../../redux/reducers/roleSlice';
import {
  getScheduleList,
  createSchedule,
  updateSchedule,
  deleteSchedule
} from '../../../../redux/reducers/scheduleSlice';

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const consultationTypes = ['Physical', 'Virtual', 'Home-Visit'];

function Schedule() {
  const [search, setSearch] = useState('');
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [delModVisible, setDelModVisible] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [form, setForm] = useState(initialFormState());

  const dispatch = useDispatch();
  
  const roleList = useSelector((state) =>
    state.role.list.filter((r) => r.type === 'role')
  );
  
  const scheduleList = useSelector((state) => state.schedule.list);

  function initialFormState() {
    return {
      name: '',
      consultationType: [],
      role: '',
      schedule: daysOfWeek.map(day => ({
        day,
        isSelected: false,
        slots: [
          { startTime: dayjs('08:00', 'HH:mm'), endTime: dayjs('20:00', 'HH:mm') }
        ]
      }))
    };
  }

  useEffect(() => {
    dispatch(getRoleList());
    dispatch(getScheduleList());
  }, [dispatch]);

  // Transform API data to component format
  // const transformApiDataToForm = (apiData) => {
  //   // Handle null/undefined apiData
  //   if (!apiData) {
  //     return initialFormState();
  //   }

  //   const consultationType = [];
  //   if (apiData.physical) consultationType.push('Physical');
  //   if (apiData.virtuals) consultationType.push('Virtual');
  //   if (apiData.homeVisit) consultationType.push('Home-Visit');

  //   const schedule = daysOfWeek.map(dayName => {
  //     const dayData = apiData.gobalDayRequest?.find(
  //       day => day.day.toLowerCase() === dayName.toLowerCase().substring(0, 3)
  //     );
      
  //     return {
  //       day: dayName,
  //       isSelected: dayData?.status || false,
  //       slots: dayData?.times?.map(time => ({
  //         startTime: dayjs(time.startTime, 'HH:mm'),
  //         endTime: dayjs(time.endTime, 'HH:mm')
  //       })) || [{ startTime: dayjs('08:00', 'HH:mm'), endTime: dayjs('20:00', 'HH:mm') }]
  //     };
  //   });

  //   return {
  //     name: apiData.templateName || '',
  //     consultationType,
  //     role: apiData.role || '',
  //     schedule
  //   };
  // };
// Transform API data to component format
const transformApiDataToForm = (apiData) => {
  // Handle null/undefined apiData
  if (!apiData) {
    return initialFormState();
  }

  const consultationType = [];
  if (apiData.physical) consultationType.push('Physical');
  if (apiData.virtuals) consultationType.push('Virtual');
  if (apiData.homeVisit) consultationType.push('Home-Visit');

  const schedule = daysOfWeek.map(dayName => {
    // Fix: Use 'globalDay' instead of 'gobalDayRequest' and match the correct day format
    const dayData = apiData.globalDay?.find(
      day => day.day.toLowerCase() === dayName.toLowerCase().substring(0, 3)
    );
    
    return {
      day: dayName,
      isSelected: dayData?.status || false,
      slots: dayData?.times?.map(time => ({
        startTime: dayjs(time.startTime, 'HH:mm'),
        endTime: dayjs(time.endTime, 'HH:mm')
      })) || [{ startTime: dayjs('08:00', 'HH:mm'), endTime: dayjs('20:00', 'HH:mm') }]
    };
  });

  return {
    name: apiData.templateName || '',
    consultationType,
    role: apiData.role || '',
    schedule
  };
};
  // Transform component data to API format
  const transformFormToApiData = (formData) => {
    const dayMapping = {
      'Sun': 'sun', 'Mon': 'mon', 'Tue': 'tue', 'Wed': 'wed',
      'Thu': 'thu', 'Fri': 'fri', 'Sat': 'sat'
    };

    return {
      physical: formData.consultationType.includes('Physical'),
      virtuals: formData.consultationType.includes('Virtual'),
      homeVisit: formData.consultationType.includes('Home-Visit'),
      templateName: formData.name,
      role: formData.role,
      gobalDayRequest: formData.schedule.map(daySchedule => ({
        day: dayMapping[daySchedule.day],
        duration: 2, // Default duration, you might want to make this configurable
        status: daySchedule.isSelected,
        times: daySchedule.slots.map(slot => ({
          startTime: slot.startTime.format('HH:mm'),
          endTime: slot.endTime.format('HH:mm')
        }))
      }))
    };
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setEditIndex(null);
    setForm(initialFormState());
  };

  const handleAddOrUpdate = () => {
    if (!form.name) return;

    const apiData = transformFormToApiData(form);

    if (editIndex !== null) {
      const scheduleToUpdate = scheduleList[editIndex];
      dispatch(updateSchedule({ 
        scheduleId: scheduleToUpdate.scheduleId, // Assuming API returns scheduleId
        scheduleData: apiData 
      })).then(() => {
        dispatch(getScheduleList());
        handleCloseDrawer();
      });
    } else {
      dispatch(createSchedule(apiData)).then(() => {
        dispatch(getScheduleList());
        handleCloseDrawer();
      });
    }
  };

  const handleEdit = (index) => {
    const item = scheduleList[index];
    const transformedData = transformApiDataToForm(item);
    setForm(transformedData);
    setEditIndex(index);
    setOpenDrawer(true);
  };

  const handleDeleteClick = (index) => {
    setDeleteIndex(index);
    setDelModVisible(true);
  };

  const handleDelConfirm = () => {
    const scheduleToDelete = scheduleList[deleteIndex];
    dispatch(deleteSchedule({ scheduleId: scheduleToDelete.scheduleId })).then(() => {
      dispatch(getScheduleList());
      setDelModVisible(false);
      setDeleteIndex(null);
    });
  };

  const handleDelModClose = () => {
    setDelModVisible(false);
    setDeleteIndex(null);
  };

  const handleCheckboxChange = (dayIndex, checked) => {
    const updated = [...form.schedule];
    updated[dayIndex].isSelected = checked;
    setForm({ ...form, schedule: updated });
  };

  const handleTimeChange = (field, value, dayIndex, slotIndex) => {
    const updated = [...form.schedule];
    updated[dayIndex].slots[slotIndex][field] = value;
    setForm({ ...form, schedule: updated });
  };

  const handleAddSlot = (dayIndex) => {
    const updated = [...form.schedule];
    updated[dayIndex].slots.push({
      startTime: dayjs('08:00', 'HH:mm'),
      endTime: dayjs('20:00', 'HH:mm'),
    });
    setForm({ ...form, schedule: updated });
  };

  const handleRemoveSlot = (dayIndex, slotIndex) => {
    const updated = [...form.schedule];
    updated[dayIndex].slots.splice(slotIndex, 1);
    setForm({ ...form, schedule: updated });
  };

  const handleConsultationChange = (type) => {
    const updated = [...form.consultationType];
    const exists = updated.includes(type);
    setForm({
      ...form,
      consultationType: exists
        ? updated.filter(t => t !== type)
        : [...updated, type]
    });
  };

  // Transform scheduleList for display
  const displayRows = scheduleList.map(item => transformApiDataToForm(item)).filter(Boolean);

  const filteredRows = displayRows.filter(row =>
    row.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap">
        <Box display="flex" gap={2} mx="auto" mb={2}>
          <TextField
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ width: '500px', backgroundColor: AppColors.lightPink }}
          />
          <Button variant="contained" onClick={() => setOpenDrawer(true)}>
            Create
          </Button>
        </Box>
        <Typography variant="h6" sx={{ ml: 'auto' }}>
          Total Schedule: {filteredRows.length}
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>S.No</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Consultation Type</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Time-Day</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">No records found</TableCell>
              </TableRow>
            ) : (
              filteredRows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.consultationType.join(', ')}</TableCell>
                  <TableCell>{row.role}</TableCell>
                  <TableCell>
                    {row.schedule.filter(d => d.isSelected).map((day, dIdx) => (
                      <div key={dIdx}>
                        <strong>{day.day}:</strong>{" "}
                        {day.slots.map((slot, sIdx) => (
                          <span key={sIdx}>
                            {slot.startTime?.format?.("HH:mm") || slot.startTime} - {slot.endTime?.format?.("HH:mm") || slot.endTime}
                            {sIdx < day.slots.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell>{scheduleList[index]?.createdDate ? new Date(scheduleList[index].createdDate).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <IconButton color="primary" size="small" onClick={() => handleEdit(index)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" size="small" onClick={() => handleDeleteClick(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Drawer */}
      <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer}>
        <Box p={3} width={360}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color="primary">
              {editIndex !== null ? "Edit Schedule" : "Create Schedule"}
            </Typography>
            <IconButton onClick={handleCloseDrawer} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            label="Schedule Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            margin="normal"
          />

          <DayWiseTimeSlots
            values={form.schedule}
            onDaySelect={handleCheckboxChange}
            onSlotChange={handleTimeChange}
            onAddSlot={handleAddSlot}
            onRemoveSlot={handleRemoveSlot}
          />

          <Typography variant="subtitle2" mt={3}>Consultation Type:</Typography>
          {consultationTypes.map(type => (
            <Box key={type} display="flex" alignItems="center" gap={1}>
              <Checkbox
                checked={form.consultationType.includes(type)}
                onChange={() => handleConsultationChange(type)}
              />
              <Typography>{type}</Typography>
            </Box>
          ))}

          <TextField
            select
            label="Role"
            fullWidth
            margin="normal"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          >
            {roleList.length === 0 ? (
              <MenuItem disabled>No roles available</MenuItem>
            ) : (
              roleList.map((role) => (
                <MenuItem key={role.roleId} value={role.roleName}>
                  {role.roleName}
                </MenuItem>
              ))
            )}
          </TextField>

          <Button fullWidth variant="contained" color="primary" sx={{ mt: 2 }} onClick={handleAddOrUpdate}>
            {editIndex !== null ? "Update" : "Add"}
          </Button>
        </Box>
      </Drawer>

      {/* Delete Modal */}
      <CustomModal
        open={delModVisible}
        onClose={handleDelModClose}
        header="Delete"
        modal
        modalWidth={30}
      >
        <Typography className="flex-center txt-regular fs14">
          Are you sure you want to delete this record?
        </Typography>
        <div className="flex-center mb10 mt15">
          <div>
            <CustomButton text="Cancel" onClick={handleDelModClose} />
          </div>
          <div className="ml10">
            <CustomButton redBtn text="Confirm" onClick={handleDelConfirm} />
          </div>
        </div>
      </CustomModal>
    </Box>
  );
}

export default Schedule;


